import { useState, useMemo, useEffect } from "react";
import { Modal } from "@mantine/core";
import {
  BiCheckCircle,
  BiInfoCircle,
  BiXCircle,
  BiLoaderAlt,
} from "react-icons/bi";

const MessageListModal = ({ messages = [], modalOpen, setModalOpen }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const perPage = 20;

  const totalPages = useMemo(
    () => Math.ceil(messages?.length / perPage),
    [messages]
  );

  const currentMessages = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    return messages?.slice(start, end) || [];
  }, [currentPage, messages]);

  const close = () => setModalOpen(false);

  const changePage = (newPage) => {
    if (newPage === currentPage) return;
    setLoading(true);
    setTimeout(() => {
      setCurrentPage(newPage);
      setLoading(false);
    }, 500);
  };

  const goNext = () => changePage(Math.min(currentPage + 1, totalPages));
  const goPrev = () => changePage(Math.max(currentPage - 1, 1));

  useEffect(() => {
    if (modalOpen) {
      setLoading(true);
      setCurrentPage(1);
      const timer = setTimeout(() => setLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  return (
    <Modal
      centered
      opened={modalOpen}
      onClose={close}
      fullScreen
      withCloseButton={false}
      zIndex={999}
      overlayProps={{ className: "email-modal-overlay" }}
      classNames={{
        content: "email-modal-content",
      }}
    >
      {loading ? (
        <div className='flex justify-center items-center h-[400px] text-gray-500 dark:text-gray-300'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : (
        <>
          {/* Modal Header */}
          <div className='w-[800px] m-auto flex justify-between items-center mb-5'>
            <h1 className='text-xl font-semibold dark:text-gray-100'>
              Messages ({messages?.length})
            </h1>
            <h1
              className='text-xl cursor-pointer dark:text-gray-100'
              onClick={close}
            >
              X
            </h1>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            <table className='m-auto w-[800px] border-collapse border border-gray-300 dark:border-gray-600'>
              <thead>
                <tr className='bg-gray-100 dark:bg-gray-900'>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    To
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    From
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Body
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Date Sent
                  </th>
                  <th className='border p-2 text-left dark:border-gray-600'>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className='text-sm'>
                {currentMessages?.length ? (
                  currentMessages.map((msg) => (
                    <tr
                      key={msg?.sid || Math.random()}
                      className='hover:bg-gray-50 dark:hover:bg-gray-800'
                    >
                      <td className='border p-2 dark:border-gray-600'>
                        {msg?.to || "-"}
                      </td>
                      <td className='border p-2 dark:border-gray-600'>
                        {msg?.from || "-"}
                      </td>
                      <td className='border p-2 dark:border-gray-600'>
                        {msg?.body || "-"}
                      </td>
                      <td className='border p-2 dark:border-gray-600'>
                        {msg?.dateSent
                          ? new Date(msg.dateSent)?.toLocaleString()
                          : "-"}
                      </td>

                      <td className='border p-2 dark:border-gray-600'>
                        <div className='flex items-center   space-x-2'>
                          {msg?.status === "Delivered" && (
                            <BiCheckCircle className='text-green-500' />
                          )}
                          {msg?.status === "Failed" && (
                            <BiXCircle className='text-red-500' />
                          )}
                          {msg?.status === "Opt-Out" && (
                            <BiInfoCircle className='text-yellow-500' />
                          )}
                          <span>{msg?.status || "-"}</span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className='border p-2 text-center' colSpan={5}>
                      No messages available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {messages?.length > perPage && (
            <div className='mt-4 m-auto w-[250px] flex justify-between items-center'>
              <button
                onClick={goPrev}
                disabled={currentPage === 1}
                className='border text-black dark:text-white px-4 py-2 text-sm hover:bg-red-600 hover:border-red-600 disabled:opacity-50'
              >
                Previous
              </button>
              <span className='text-sm text-gray-800 dark:text-gray-200'>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={goNext}
                disabled={currentPage === totalPages}
                className='border text-black dark:text-white px-4 py-2 text-sm hover:bg-red-600 hover:border-red-600 disabled:opacity-50'
              >
                Next
              </button>
            </div>
          )}

          {/* Close Button */}
          <div className='mt-4 flex justify-end'>
            <button
              className='rounded-lg bg-red-600 px-4 py-2 text-sm text-gray-100 hover:bg-red-800'
              onClick={close}
            >
              Close
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default MessageListModal;
